import { useRef, type ReactNode } from 'react';
import { Icon } from '../../icons';
import VideoRenderer, { type VideoRendererProps, type StreamSource } from './VideoRenderer';
import FullscreenButton from './FullscreenButton';
import { useFullscreen } from './fullscreen';
import { tileFeed } from './gridPolicy';
import { privacyLabel, type PrivacyMode } from './aiLayers';
import './VideoGrid.css';
import './privacyBadge.css';

interface VideoGridProps extends Omit<VideoRendererProps, 'activeSourceId' | 'detectionEnabled'> {
  gridSourceIds: string[];
  primarySourceId: string;
  streamSources: StreamSource[];
  onSetPrimary: (id: string) => void;
  onRemoveFromGrid: (id: string) => void;
  detectionEnabled: boolean;
  activeSourceId: string;
  renderCell?: (sourceId: string, isPrimary: boolean) => ReactNode | null;
  getCellLabel?: (sourceId: string) => string;
  /**
   * The model running on one cell's feed, or null.
   *
   * Asked per cell, not once for the grid: with per-feed AI channels each feed
   * composites its own overlay, so a cell showing a model name is a cell whose
   * picture really carries it. See
   * docs/superpowers/specs/2026-08-27-per-feed-ai-channels-design.md.
   */
  modelNameFor?: (sourceId: string) => string | null;
  /** The privacy mode on one cell's feed; absent means every cell is `picture`. */
  privacyFor?: (sourceId: string) => PrivacyMode;
}

/**
 * Phone and DJI cells detect in the browser and draw their own boxes, so the
 * server-rendered marketplace overlay (heatmap, intrusion zone, PPE colouring)
 * never reaches them — only the swapped weights do.
 */
function isClientDetected(type?: string): boolean {
  return type === 'phone' || type === 'dji';
}

function getLayoutClass(count: number): string {
  if (count <= 1) return 'video-grid-1';
  if (count <= 2) return 'video-grid-2';
  if (count <= 4) return 'video-grid-4';
  if (count <= 6) return 'video-grid-6';
  return 'video-grid-9';
}

// The grid's fps/quality numbers and the single-tile rule live in gridPolicy.ts.
export {
  GRID_FEED_FPS, GRID_FEED_QUALITY, GRID_PRIMARY_FEED_FPS, GRID_PRIMARY_FEED_QUALITY,
} from './gridPolicy';

export default function VideoGrid({
  gridSourceIds,
  primarySourceId,
  streamSources,
  onSetPrimary,
  onRemoveFromGrid,
  detectionEnabled,
  renderCell,
  getCellLabel,
  modelNameFor,
  privacyFor,
  ...rendererProps
}: VideoGridProps) {
  const layoutClass = getLayoutClass(gridSourceIds.length);

  return (
    <div className={`video-grid ${layoutClass}`}>
      {gridSourceIds.map((sourceId) => {
        const source = streamSources.find(s => s.id === sourceId);
        const isPrimary = sourceId === primarySourceId;
        // A cell shows detection when ITS OWN feed carries a model. The primary
        // cell falls back to the panel-wide flag for deployments with no
        // channel state (an older backend, or the endpoint unreachable).
        const cellModel = modelNameFor ? modelNameFor(sourceId) : null;
        return (
          <VideoGridCell
            /* Keyed by source, so the cell's <img> survives the layout changing
               around it (1 tile -> 2 tiles changes its props, not its identity). */
            key={sourceId}
            sourceId={sourceId}
            source={source}
            isPrimary={isPrimary}
            tileCount={gridSourceIds.length}
            label={getCellLabel ? getCellLabel(sourceId) : (source?.name || sourceId)}
            customCell={renderCell ? renderCell(sourceId, isPrimary) : null}
            cellModel={cellModel}
            cellPrivacy={privacyFor ? privacyFor(sourceId) : 'picture'}
            cellDetecting={cellModel !== null || (isPrimary && detectionEnabled && !modelNameFor)}
            onSetPrimary={onSetPrimary}
            onRemoveFromGrid={onRemoveFromGrid}
            rendererProps={rendererProps}
          />
        );
      })}
    </div>
  );
}

type GridRendererProps = Omit<VideoRendererProps,
  'activeSourceId' | 'detectionEnabled' | 'activeStreamSource' | 'feedFps' | 'feedQuality' | 'preferWebrtc'>;

interface VideoGridCellProps {
  sourceId: string;
  source?: StreamSource;
  isPrimary: boolean;
  tileCount: number;
  label: string;
  customCell: ReactNode | null;
  cellModel: string | null;
  cellPrivacy: PrivacyMode;
  cellDetecting: boolean;
  onSetPrimary: (id: string) => void;
  onRemoveFromGrid: (id: string) => void;
  rendererProps: GridRendererProps;
}

function VideoGridCell({
  sourceId, source, isPrimary, tileCount, label, customCell, cellModel, cellPrivacy, cellDetecting,
  onSetPrimary, onRemoveFromGrid, rendererProps,
}: VideoGridCellProps) {
  // The whole cell goes full screen — picture plus its label bar, so the exit
  // control is still on screen — and the renderer inside is not remounted.
  const cellRef = useRef<HTMLDivElement>(null);
  const fs = useFullscreen(cellRef);
  const feed = tileFeed(tileCount, isPrimary);

  return (
    <div
      ref={cellRef}
      className={`video-grid-cell ${isPrimary ? 'primary' : ''}`}
      onClick={() => !isPrimary && onSetPrimary(sourceId)}
    >
      {customCell !== null ? customCell : (
        <VideoRenderer
          {...rendererProps}
          activeSourceId={sourceId}
          activeStreamSource={source}
          detectionEnabled={cellDetecting}
          feedFps={feed.feedFps}
          feedQuality={feed.feedQuality}
          preferWebrtc={feed.preferWebrtc}
        />
      )}
      <div className="video-grid-cell-label">
        <span className="video-grid-cell-name">
          {label}
          {cellDetecting && <span className="video-grid-detect-badge"><Icon name="detect" /></span>}
          {cellModel && (
            <span
              className="video-grid-model-badge"
              title={isClientDetected(source?.type)
                ? `${cellModel} — boxes only on this feed; the full overlay needs a server-processed source`
                : cellModel}
            >
              {cellModel}
            </span>
          )}
          {cellPrivacy !== 'picture' && (
            <span className={`privacy-badge ${cellPrivacy === 'blur' ? 'blur' : ''}`}>
              {privacyLabel(cellPrivacy)}
            </span>
          )}
        </span>
        <span className="video-grid-cell-actions">
          {fs.supported && (
            <FullscreenButton
              className="video-grid-cell-btn"
              active={fs.active}
              onToggle={fs.toggle}
            />
          )}
          {!fs.active && (
            <button
              className="video-grid-cell-btn video-grid-cell-remove"
              onClick={(e) => { e.stopPropagation(); onRemoveFromGrid(sourceId); }}
              title="Remove from grid"
              aria-label="Remove"
            >
              <Icon name="clear" />
            </button>
          )}
        </span>
      </div>
    </div>
  );
}
